import './styles.scss';

import { Col, Row } from 'react-bootstrap';

import { AutomationTripsTypes } from '@/features/Automation/pages/AutomationTrips/types';
import DateInput from '@/common/DateInput';
import Form from '@/common/Form';
import { ReactElement } from 'react';
import SelectAirport from '@/common/SelectAirport';
import { getClasses } from '@/utils';
import { useAutomationTrips } from '@/features/Automation/pages/AutomationTrips';
import useSession from '@/state/session';

const AutomationTripsFilters = (): ReactElement => {
  const [{ hydrated, filters, loading, processing, preview, runAutomation, onReset }] = useAutomationTrips(({ state, hydrated }) => [
    {
      hydrated,
      filters: state.filters,
      loading: state.loading,
      processing: state.processing,
      preview: state.preview,
      runAutomation: state.runAutomation,
      onReset: state.onReset,
    },
  ]);

  if (!hydrated) return null;
  return (
    <Form
      className="AutomationTripsFilters"
      value={{ ...filters, market: filters.market ?? useSession.getState().state.user.primaryAirportCode }}
      onSubmit={!loading ? (values) => preview({ ...filters, ...values }) : undefined}
      validator={AutomationTripsTypes.validator}
    >
      <Row>
        <Form.Watch name={['from', 'to']}>
          {([from, to]: string[], form) => (
            <>
              <Col xs={12} md={1}>
                <DateInput
                  name="from"
                  disabled={loading}
                  value={from}
                  onChange={(val) => {
                    form.setValue('from', val);
                    form.trigger();
                  }}
                  isValid={!form.getFieldState('from').invalid}
                  isInvalid={form.getFieldState('from').invalid}
                />
              </Col>
              <Col xs={12} md={1}>
                <DateInput
                  name="to"
                  disabled={loading}
                  value={to}
                  onChange={(val) => {
                    form.setValue('to', val);
                    form.trigger();
                  }}
                  isValid={!form.getFieldState('to').invalid}
                  isInvalid={form.getFieldState('to').invalid}
                />
              </Col>
            </>
          )}
        </Form.Watch>
        <Col xs={12} md={1}>
          <Form.Watch name="market">
            {([market]: string, form) => (
              <SelectAirport
                group
                name="market"
                value={market}
                multiple
                filter={(options) => {
                  // Do not return single airports that are included in a group.
                  const groups = options
                    .filter((option) => option.values.length > 1)
                    .sort((a, b) => a.displayName.localeCompare(b.displayName));
                  const singles = options
                    .filter(({ values }) => values.length === 1)
                    .sort((a, b) => a.displayName.localeCompare(b.displayName));
                  const singleValues = singles.map(({ values }) => values[0]);
                  if (market && !singleValues.includes(market)) {
                    for (const group of groups) {
                      if (group.values.includes(market)) {
                        const groupValue = group.values.join(',');
                        form.setValue('market', groupValue);
                        form.trigger();
                        AutomationTripsTypes.initFilters.market = groupValue;
                      }
                    }
                  }
                  return [...groups, ...singles];
                }}
                onChange={(val: string) => {
                  const markets = market ? market.split(',') : [];
                  const values = val.split(',');
                  const result = (
                    values.every((value) => markets.includes(value))
                      ? markets.filter((value) => !values.includes(value))
                      : [...new Set([...markets, ...values])]
                  ).join(',');
                  form.setValue('market', result);
                  form.trigger();
                }}
                onFocus={() => {
                  form.setValue('market', '');
                  form.trigger();
                }}
                disabled={loading}
                isValid={!form.getFieldState('market').invalid}
                isInvalid={form.getFieldState('market').invalid}
                placeholder="Market..."
              />
            )}
          </Form.Watch>
        </Col>
        <Col xs={12} md={1} className="d-flex gap-2">
          <Form.Watch>
            {(_, form) => (
              <>
                <Form.Button type="submit" variant="success" disabled={loading || form.getFieldState('market').invalid}>
                  {loading && (
                    <span>
                      <i className="fa fa-spinner fa-pulse" />
                    </span>
                  )}
                  {!loading && <span>GO</span>}
                </Form.Button>
                <Form.Button type="button" variant="light" className="border" onClick={onReset(form)}>
                  Reset
                </Form.Button>
              </>
            )}
          </Form.Watch>
        </Col>
        <Col className="d-flex flex-grow-1 justify-content-between" xs={12} md={8}>
          <Form.Watch>
            {(_, form) => {
              const invalidFrom = form.getFieldState('from').invalid;
              const invalidTo = form.getFieldState('to').invalid;
              const invalidMarket = form.getFieldState('market').invalid;
              const valid = !invalidFrom && !invalidTo && !invalidMarket;
              return (
                <span className={getClasses('d-flex flex-column justify-content-center', !valid ? 'text-danger' : undefined)}>
                  {(invalidFrom && <>Starting date must be in the future.</>) ||
                    (invalidTo && <>Selected date range must be at most 3 days.</>) ||
                    (valid && <>Select dates, market, and hit go to choose trips for automation.</>)}
                </span>
              );
            }}
          </Form.Watch>
          <Form.Button type="button" variant="success" onClick={runAutomation} disabled={!!processing}>
            <span>
              <i className="sv sv-power" />
            </span>
            <span>Run Automation</span>
          </Form.Button>
        </Col>
      </Row>
    </Form>
  );
};

export default AutomationTripsFilters;
